import * as React from 'react';
import { ReactElement } from 'react';
import { Block } from '../../components/layout/Block';
import { Props } from './props';
import { CheckPermissionFunction, PermissionGate } from '../../../../Auth/components/PermissionGate';
import { getVocabularyOptions } from '../../../../Vocabulary/util';
import { IntlShape, useIntl } from 'react-intl';
import { LabelledInputText } from '../../../components/form/LabelledInputText';
import { Checkbox, CheckboxChangeEvent } from 'primereact/checkbox';
import { GeoLocations } from '../../components/parts/GeoLocations';
import { WorkGeoLocationDTO } from '../../../../api/core';
import Label from '../../../components/Label';
import { LabelledDropdown } from '../../../components/form/LabelledDropdown';

export const SpecificPage = (props: Props): ReactElement => {
  const intl: IntlShape = useIntl();
  return <Block title={`work.header.specific`}>
    <PermissionGate always={false} entity={props.permissionEntity} field={'publication_number'}>
      {(checkPermission: CheckPermissionFunction): ReactElement =>
        <div className={'col-12'}>
          <LabelledInputText
            id={'publicationNumber'}
            autoFocus={true}
            value={props.modifiedModel.publicationNumber}
            permissions={checkPermission()}
            validation={props.validation?.root?.fields?.publication_number}
            onChange={(publicationNumber: string): void => props.setField('publicationNumber', publicationNumber)}
            title={'work.attr.publicationNumber'}
          />
        </div>
      }
    </PermissionGate>
    <PermissionGate always={false} entity={props.permissionEntity} field={'application_number'}>
      {(checkPermission: CheckPermissionFunction): ReactElement =>
        <div className={'col-12'}>
          <LabelledInputText
            id={'applicationNumber'}
            value={props.modifiedModel.applicationNumber}
            permissions={checkPermission()}
            validation={props.validation?.root?.fields?.application_number}
            onChange={(applicationNumber: string): void => props.setField('applicationNumber', applicationNumber)}
            title={'work.attr.applicationNumber'}
          />
        </div>
      }
    </PermissionGate>
    <PermissionGate always={false} entity={props.permissionEntity} field={'application_country'}>
      {(checkPermission: CheckPermissionFunction): ReactElement =>
        <div className={'col-12'}>
          <LabelledDropdown
            id={'applicationCountry'}
            value={props.modifiedModel.applicationCountry}
            permissions={checkPermission()}
            validation={props.validation?.root?.fields?.application_country}
            onChange={(applicationCountry: string): void => props.setField('applicationCountry', applicationCountry)}
            title={'work.attr.applicationCountry'}
            options={getVocabularyOptions(intl.locale, 'country_list', props.pubtypeGroup)}
          />
        </div>
      }
    </PermissionGate>
    <PermissionGate entity={props.permissionEntity} field="priority_patent_application">
      {(checkPermission: CheckPermissionFunction) =>
        <div className={'col-12 flex'}>
          <h4 className={'font-bold flex-grow-1'}><Label
            label={intl.formatMessage({id: 'work.attr.priorityPatentApplication.label'})}
            htmlFor={'priorityPatentApplication'}/>
          </h4>
          <Checkbox
            inputId={'priorityPatentApplication'}
            checked={props.modifiedModel.priorityPatentApplication || false}
            disabled={!checkPermission().canWrite}
            invalid={!props.validation?.root?.fields?.priority_patent_application.valid}
            required={checkPermission().required}
            onChange={(e: CheckboxChangeEvent): void => props.setField('priorityPatentApplication', e.checked)}
          />
        </div>
      }
    </PermissionGate>
    <PermissionGate always={false} entity={props.permissionEntity} field={'priority_country'}>
      {(checkPermission: CheckPermissionFunction): ReactElement =>
        <div className={'col-12'}>
          <LabelledDropdown
            id={'priorityCountry'}
            value={props.modifiedModel.priorityCountry}
            permissions={checkPermission()}
            validation={props.validation?.root?.fields?.priority_country}
            onChange={(priorityCountry: string): void => props.setField('priorityCountry', priorityCountry)}
            title={'work.attr.priorityCountry'}
            options={getVocabularyOptions(intl.locale, 'country_list', props.pubtypeGroup)}
          />
        </div>
      }
    </PermissionGate>
    <PermissionGate always={false} entity={props.permissionEntity} field={'priority_number'}>
      {(checkPermission: CheckPermissionFunction): ReactElement =>
        <div className={'col-12'}>
          <LabelledInputText
            id={'priorityNumber'}
            value={props.modifiedModel.priorityNumber}
            permissions={checkPermission()}
            validation={props.validation?.root?.fields?.priority_number}
            onChange={(priorityNumber: string): void => props.setField('priorityNumber', priorityNumber)}
            title={'work.attr.priorityNumber'}
          />
        </div>
      }
    </PermissionGate>
    <PermissionGate always={false} entity={props.permissionEntity} field={'grant_number'}>
      {(checkPermission: CheckPermissionFunction): ReactElement =>
        <div className={'col-12'}>
          <LabelledInputText
            id={'grantNumber'}
            value={props.modifiedModel.grantNumber}
            permissions={checkPermission()}
            validation={props.validation?.root?.fields?.grant_number}
            onChange={(grantNumber: string): void => props.setField('grantNumber', grantNumber)}
            title={'work.attr.grantNumber'}
          />
        </div>
      }
    </PermissionGate>
    <PermissionGate always={false} entity={props.permissionEntity} field={'patent_family'}>
      {(checkPermission: CheckPermissionFunction): ReactElement =>
        <div className={'col-12'}>
          <LabelledInputText
            id={'patentFamily'}
            value={props.modifiedModel.patentFamily}
            permissions={checkPermission()}
            validation={props.validation?.root?.fields?.patent_family}
            onChange={(patentFamily: string): void => props.setField('patentFamily', patentFamily)}
            title={'work.attr.patentFamily'}
          />
        </div>
      }
    </PermissionGate>
    <PermissionGate always={false} entity={props.permissionEntity} field={'peer_reviewed'}>
      {(checkPermission: CheckPermissionFunction): ReactElement =>
        <div className={'col-12'}>
          <LabelledDropdown
            id={'peerReviewed'}
            value={props.modifiedModel.peerReviewed}
            permissions={checkPermission()}
            validation={props.validation?.root?.fields?.peer_reviewed}
            onChange={(peerReviewed: string): void => props.setField('peerReviewed', peerReviewed)}
            title={'work.attr.peerReviewed'}
            options={getVocabularyOptions(intl.locale, 'country_list', props.pubtypeGroup)}
          />
        </div>
      }
    </PermissionGate>
    <PermissionGate always={false} entity={props.permissionEntity} field={'patent_id'}>
      {(checkPermission: CheckPermissionFunction): ReactElement =>
        <div className={'col-12'}>
          <LabelledInputText
            id={'patentId'}
            value={props.modifiedModel.patentId}
            permissions={checkPermission()}
            validation={props.validation?.root?.fields?.patent_id}
            onChange={(patentId: string): void => props.setField('patentId', patentId)}
            title={'work.attr.patentId'}
          />
        </div>
      }
    </PermissionGate>
    <PermissionGate always={false} entity={props.permissionEntity} field={'patent_type'}>
      {(checkPermission: CheckPermissionFunction): ReactElement =>
        <div className={'col-12'}>
          <LabelledDropdown
            id={'patentType'}
            value={props.modifiedModel.patentType}
            permissions={checkPermission()}
            validation={props.validation?.root?.fields?.patent_type}
            onChange={(patentType: string): void => props.setField('patentType', patentType)}
            title={'work.attr.patentType'}
            options={getVocabularyOptions(intl.locale, 'affiliations_list', props.pubtypeGroup)}
          />
        </div>
      }
    </PermissionGate>
    <PermissionGate always={false} entity={props.permissionEntity} field={'work_geo_locations'}>
      {(checkPermission: CheckPermissionFunction): ReactElement =>
        <div className={'col-12'}>
          <GeoLocations
            autoFocus={props.autoFocus}
            workGeoLocations={props.modifiedModel.workGeoLocations || []}
            checkPermission={checkPermission}
            validation={props.validation?.root?.fields?.work_geo_locations}
            onChange={(workGeoLocations: WorkGeoLocationDTO[]): void => props.setField('workGeoLocations', workGeoLocations)}
            pubtypeGroup={props.pubtypeGroup}
          />
        </div>
      }
    </PermissionGate>
  </Block>;
};
